import { Popover } from "radix-ui";
import { useQuery } from "@tanstack/react-query";
import { get } from "../../api/util";
import Spinner from "../spinner/Spinner";
import IconCheckCircleChecked from "../icons/IconCheckCircleChecked";
import { useRef, useState } from "react";
import { ErklaeringType } from "../popover/TLinkButton";

const normalizeBaseUrl = (baseUrl?: string): string => {
  if (!baseUrl?.trim()) return "";

  return `${baseUrl.trim().replace(/\/+$/, "")}/`;
};

const TLink = ({
  id,
  type,
  heading,
}: {
  id: string;
  type: ErklaeringType;
  heading: string;
}) => {
  // state and refs
  const [isCopied, setIsCopied] = useState(false);
  const ref = useRef(null);
  const linkRoute =
    type === "klienterklaering"
      ? "link"
      : type === "tilsynserklaering"
        ? "tilsynslink"
        : "!error!";

  // fetching things
  const { data, status } = useQuery<IIndberetningDTO>({
    queryKey: [`link(${id})`],
    queryFn: async function () {
      return get({
        query: `indberetning/${type}/${id}`,
      });
    },
    retry: false,
    refetchOnMount: true,
  });

  // business
  const indberetningBaseUrl = normalizeBaseUrl(
    import.meta.env.VITE_INDBERETNING_ENDPOINT,
  );
  const link = data?.loginToken
    ? `${indberetningBaseUrl}${linkRoute}/${data.loginToken}`
    : "";
  const hasIndberetningBaseUrl = indberetningBaseUrl.length > 0;

  // events
  function copyLinkHandler() {
    if (!link) return;

    navigator.clipboard.writeText(link).then(() => {
      setIsCopied(true);

      // Reset the copied state after 5 seconds
      setTimeout(() => {
        setIsCopied(false);
      }, 5000);
    });

    if (ref.current) {
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(ref.current);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }

  return (
    <Popover.Content className="c-popover" align="end">
      <div className="c-popover-content p-4" style={{ width: "40rem" }}>
        <h3 className="h5 mb-2 lh-sm">{heading}</h3>
        <div className="bg-light d-flex p-2 w-100">
          <span
            ref={ref}
            className="d-flex flex-row align-items-center lh-sm fw-normal px-2 py-1 text-break"
          >
            {(() => {
              switch (status) {
                case "error":
                  return (
                    <span style={{ opacity: "0.75" }}>
                      Linket er ikke oprettet, da{" "}
                      {type === "klienterklaering"
                        ? "klientkontoerklæringen"
                        : "Tilsynserklæringen"}{" "}
                      endnu ikke er lagt ud på Indberetningsportalen.
                    </span>
                  );
                case "success":
                  return hasIndberetningBaseUrl ? (
                    <>{link}</>
                  ) : (
                    <span style={{ opacity: "0.75" }}>
                      Linket kan ikke vises endnu, fordi{" "}
                      <code>VITE_INDBERETNING_ENDPOINT</code> mangler i
                      frontendens konfiguration.
                    </span>
                  );
                case "pending":
                  return <Spinner inline size={16} label="Henter link ..." />;
              }
            })()}
          </span>
          <button
            type="button"
            className="btn btn-primary d-flex align-items-center gap-2 text-nowrap justify-content-center ms-auto"
            style={{ minWidth: 130 }}
            onClick={copyLinkHandler}
            disabled={status !== "success" || !hasIndberetningBaseUrl}
          >
            {isCopied ? (
              <>
                <IconCheckCircleChecked height={15} />
                <span>Kopieret!</span>
              </>
            ) : (
              <span>Kopier link</span>
            )}
          </button>
        </div>
        <Popover.Arrow className="c-dropdown-menu-arrow" />
      </div>
    </Popover.Content>
  );
};

export default TLink;
